import { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Navigation from '../components/Navigation'
import { useAuth } from '../context/AuthContext'
import { trackPurchase } from '../utils/analytics'

export default function PaymentSuccessPage() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [redirectCountdown, setRedirectCountdown] = useState(8)
  const [isPaused, setIsPaused] = useState(false)
  const [showConfetti, setShowConfetti] = useState(true)
  const hasTracked = useRef(false)

  const sessionId = searchParams.get('session_id')
  const packageType = searchParams.get('package_type')
  const amount = searchParams.get('amount')

  useEffect(() => {
    if (!loading && !user) {
      navigate('/')
      return
    }
  }, [user, loading, navigate])

  // Countdown timer - pauses when hovering
  useEffect(() => {
    if (isPaused) return

    const timer = setInterval(() => {
      setRedirectCountdown(prev => prev > 0 ? prev - 1 : 0)
    }, 1000)

    return () => clearInterval(timer)
  }, [isPaused])

  // Navigate when countdown reaches 0
  useEffect(() => {
    if (redirectCountdown === 0) {
      navigate('/dashboard')
    }
  }, [redirectCountdown, navigate])

  // Hide confetti after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 3000)
    return () => clearTimeout(timer)
  }, [])

  // Track purchase conversion (only once)
  useEffect(() => {
    if (hasTracked.current) return
    if (!packageType || !amount) return

    hasTracked.current = true
    const amountValue = parseFloat(amount)
    trackPurchase(amountValue, packageType)
  }, [packageType, amount])

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navigation />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
        {/* Minimal Confetti */}
        {showConfetti && (
          <div className="fixed inset-0 pointer-events-none overflow-hidden z-10">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: '-10px',
                  width: '8px',
                  height: '8px',
                  background: ['#10b981', '#3b82f6', '#f59e0b', '#ec4899'][i % 4],
                  borderRadius: i % 3 === 0 ? '50%' : '0%',
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${2 + Math.random() * 1}s`,
                  opacity: 0.7
                }}
              />
            ))}
          </div>
        )}

        <div className="text-center relative z-20">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center border-4 border-green-300 dark:border-green-700">
              <svg className="w-14 h-14 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            </div>
          </div>

          {/* Success Message */}
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Payment Successful! 🎉
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Your credit purchase has been completed successfully.
          </p>

          {/* Session ID */}
          {sessionId && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-8 text-left max-w-md mx-auto">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 font-medium">Session ID:</p>
              <p className="text-sm font-mono bg-white dark:bg-gray-700 p-2 rounded break-all text-gray-900 dark:text-white">
                {sessionId}
              </p>
            </div>
          )}

          {/* What's Next */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-bold text-blue-900 dark:text-blue-100 mb-3">What's Next?</h3>
            <ul className="space-y-2 text-sm text-blue-900 dark:text-blue-200">
              <li>✓ Your credits have been added to your account</li>
              <li>✓ You can now create processing jobs immediately</li>
              <li>✓ Check your billing history in the dashboard</li>
              <li>✓ No expiration - use credits anytime</li>
            </ul>
          </div>

          {/* Redirect Info */}
          <p
            className="text-gray-600 dark:text-gray-400 mb-6 cursor-help"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            title="Hover to pause countdown"
          >
            Redirecting to dashboard in{' '}
            <span className="font-bold text-primary-600 dark:text-primary-400">
              {redirectCountdown}
            </span>
            {' '}seconds...
            {isPaused && (
              <span className="ml-2 text-xs text-yellow-600 dark:text-yellow-400">
                (paused)
              </span>
            )}
          </p>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Go to Dashboard Now
            </button>
            <button
              onClick={() => navigate('/')}
              className="border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
